/**
 * Deep links (QM-D19-002). Notification links are portal-relative paths. When the session has expired the
 * app sends the user to sign-in with ?next=<path> and returns to the item after re-auth. `next` is only
 * honoured for same-origin relative paths (no open redirect, no protocol-relative or backslash tricks).
 */
const SAFE_PATH = /^\/(?![/\\])[A-Za-z0-9/_\-.?=&%]*$/;

export function safeNext(next: string | null | undefined, fallback = '/'): string {
  if (!next) return fallback;
  if (!SAFE_PATH.test(next) || next.includes('\\')) return fallback;
  if (isAuthPath(next) || next.startsWith('/bff')) return fallback;
  return next;
}

/** Standalone sign-in / sign-up pages. `/sign-in/link` is the magic-link landing (fragment token). */
export const isAuthPath = (path: string): boolean => /^\/(signin|signup|sign-in)(?=$|[/?#])/.test(path);

/**
 * Paths that render without a session and must not be bounced to sign-in.
 * Partner `/apply` is public. `/view-as` (Super Admin view-as landing) creates the session, so it is public on the
 * customer and partner portals. Admin has no public page besides sign-in.
 */
export function isPublicPath(surface: 'customer' | 'partner' | 'admin', path: string): boolean {
  if (isAuthPath(path)) return true;
  if (surface === 'customer') return /^\/(invite|join|accept-invite|view-as)(?=$|[/?#])/.test(path);
  if (surface === 'partner') return /^\/(apply|view-as)(?=$|[/?#])/.test(path);
  return false;
}

/** Customer paths that work without a session (sign-in, sign-up, invite acceptance): never bounced to sign-in. */
export const isPublicCustomerPath = (path: string): boolean => isPublicPath('customer', path);

export function signInUrl(currentPath: string, search = ''): string {
  const next = safeNext(`${currentPath}${search}`, '/');
  return next === '/' ? '/signin' : `/signin?next=${encodeURIComponent(next)}`;
}

/**
 * Forced sign-out / anonymous bounce. Every surface serves `/signin` (and `/sign-in`), so the target
 * exists and `?next=` can round-trip. Public pages (partner apply, invite acceptance) stay put.
 */
export function signOutUrl(surface: 'customer' | 'partner' | 'admin', currentPath: string, search = ''): string {
  if (isPublicPath(surface, currentPath)) return '/signin';
  return signInUrl(currentPath, search);
}

/** Magic-link emails put the single-use token in the URL fragment (`/sign-in/link#token=`). */
export function tokenFromHash(hash: string): string | null {
  const m = /(?:^#|&)token=([A-Za-z0-9_-]{20,200})(?:&|$)/.exec(hash);
  return m?.[1] ?? null;
}

/** Survives the Entra redirect and the magic-link email round-trip (same browser). Never an open redirect. */
export const AUTH_NEXT_KEY = 'qm.auth.next';

export function rememberNext(next: string | null | undefined): void {
  if (next == null || next === '') return;
  const safe = safeNext(next);
  try {
    if (safe === '/') sessionStorage.removeItem(AUTH_NEXT_KEY);
    else sessionStorage.setItem(AUTH_NEXT_KEY, safe);
  } catch { /* storage blocked */ }
}

export function takeNext(fallback = '/'): string {
  let stored = '';
  try {
    stored = sessionStorage.getItem(AUTH_NEXT_KEY) ?? '';
    sessionStorage.removeItem(AUTH_NEXT_KEY);
  } catch { /* storage blocked */ }
  return safeNext(stored, fallback);
}

/** `?next=` on this page wins; otherwise the value stored before leaving for Entra or the inbox. */
export function authDestination(queryNext: string): string {
  if (queryNext !== '/') {
    try { sessionStorage.removeItem(AUTH_NEXT_KEY); } catch { /* storage blocked */ }
    return queryNext;
  }
  return takeNext(queryNext);
}
